import { useLayoutEffect, useRef, useState } from "react";
import { DynamicFormStateType, InitializeObjectType, PageDescriptionType, useDynamicFormHandlerType } from ".";
import ScriptMediator from "./ScriptEngine";
import { ClientJsonParser } from "./ScriptEngine/Utils/ClientJsonParser";
import buildViewModel, { FormViewModel, PageContainerViewModel } from "./Models";
import BaseViewModel from "./Models/BaseViewModel";
import ViewModelDataCollector from "./ScriptEngine/ViewModelDataCollector";
import { RubyControl } from "src/Services";

const initState = { 
	loaded: false, 
	data: {}, 
	pages: [],
	fileInfos: []
};

const useDynamicForm: useDynamicFormHandlerType = () => {

	const mediator = useRef<ScriptMediator>();
	const [ formState, setFrmState ] = useState<DynamicFormStateType>(initState);

	/** При прогрузки ДОМ-дерева, грузим Руби-скрипты */
	useLayoutEffect( () => {
		RubyControl.initRuby();
		// При размонтировании удаляем их
		return () => RubyControl.unInitRuby();
	}, [] );
	
	/** Подготовка данных данных */
	const prepareData = ( param: InitializeObjectType, mediator: ScriptMediator ) => {
		try {
			const formContextData = JSON.parse( param?.formContextData ?? "{}", ClientJsonParser.parse );
			const dataContext = JSON.parse( param.dataContext, ClientJsonParser.parse );
			console.timeLog( "InitDynamicForm", "Парсинг JSON" );
			BaseViewModel.mediator = mediator;
			BaseViewModel.formContextData = formContextData;
			BaseViewModel.dataContext = dataContext;
			const sizesRaw = param.attachmentsWithSizes ?? {};
			const normalized: Record<string, number> = {};
			Object.keys(sizesRaw).forEach(k => {
				const v = sizesRaw[k];
				const n = typeof v === "number" ? v : parseInt(String(v), 10);
				if ( Number.isFinite(n) && n > 0 ) normalized[k] = n;
			});
			BaseViewModel.attachmentsWithSizes = normalized;
			BaseViewModel.fileId = param.fileId;
			BaseViewModel.componentId = param.componentId;
			BaseViewModel.typename = param.typename;
			BaseViewModel.EntityPathToViewModel = {};
			BaseViewModel.scanSettings = param.scanSettings;
			mediator.onSetPropertyValue = onSetPropertyValue;
			// console.log( "formContextData",  formContextData );
			// console.log( "dataContext",  dataContext );
			const model = buildViewModel( param.formDescription, dataContext );
			console.timeLog( "InitDynamicForm", "Построение модели" );
			// при наличии шагов запускаем скрипты только на текущей странице
			if ( model?.FormElements?.[0]?.ControlKey === "PageContainer" ) {
				mediator.setCurrentPageIndex(0);
			}
			mediator.onViewModelBuilded( model );
			// инициализируем видимость шагов по DisplayScript для всех страниц
			if ( typeof mediator.initPagesVisibility === "function" ) {
				mediator.initPagesVisibility();
			}
			return model;
		}
		catch ( error: any ) {
			console.warn( error );
		}
	};

	const initForm = ( param: InitializeObjectType, readOnly: boolean ) => new Promise<string>( ( resolve , reject ) => {
		console.time( "InitDynamicForm" );
		mediator.current = new ScriptMediator( readOnly );
		console.timeLog( "InitDynamicForm", "Инициация медиатора" );
		const model = prepareData( param, mediator.current );
		console.timeLog( "InitDynamicForm", "Обработка модули медиатором" );
		
		if ( !model )
			throw Error( "Ошибка при генерации модели данных" );

		// собираем initialValues для Formik из построенной модели
		const initialData = ViewModelDataCollector.collectFormDataKoVm( model );
		const formValues = checkIsPages(model)
			? (initialData as Record<string, unknown>)["_"]
			: initialData;

		setFrmState( prev => ({
			...prev,
			rootModel: model,
			data: formValues || {},
			pages: getPagesDescr(model),
			currentPage: 0,
			loaded: true
		}));
		console.timeEnd("InitDynamicForm");
		resolve( "success" );
	});

	const checkIsPages = ( model: FormViewModel ) => {
		if ( !model ) return false;
		return model.FormElements[0].ControlKey === "PageContainer";
	};

	const getPagesDescr = ( model: FormViewModel ): Array<PageDescriptionType> => {
		const pages: Array<PageDescriptionType> = [];
		if ( checkIsPages(model) ) {
			const pageContainer = model.FormElements[0] as PageContainerViewModel;
			for ( let index = 0; index < pageContainer.Pages.length; index++ ) {
				const page = pageContainer.Pages[index];
				pages.push({
					index,
					title: page.Caption,
					status: page.IsControlVisible() ? "" : "hidden"
				});
			}
		}
		return pages;
	};

	/** Необходима фиксировать изменения состояния вызванных в скриптах... */
	const onSetPropertyValue = ( element: BaseViewModel, propertyName: string, value: any ) => {

		setFrmState( prev => {
			let _pages = [ ...formState.pages ];
			_pages = getPagesDescr( element.getRootElement() as FormViewModel );			
			return {
				...prev,
				pages: _pages,
				data: {
					...prev.data,
					[element.Path]: {
						...prev.data[element.Path],
						[propertyName]: value
					}
				}
			};
		});
	};

	const resetForm = () => {
		setFrmState( initState );
		ViewModelDataCollector.reset();
	};

	const formValidate = ( pageIndex: number = 0 ) => {
		if ( checkIsPages( formState.rootModel! ) ) {
			const pageContainer = formState.rootModel?.FormElements[0] as PageContainerViewModel;
			const pageError = pageContainer.Pages[pageIndex].Validate();
			if ( !pageError ) {
				setFrmState( prev => {
					const newState = { ...prev };
					newState.pages[pageIndex].status = "error";
					return newState;
				});
			}
			else {
				setFrmState( prev => {
					const newState = { ...prev };
					newState.pages[pageIndex].status = "complete";
					return newState;
				});
			}
			return !!formState.rootModel?.Validate();
		}
		else 
			return !!formState.rootModel?.Validate();
	};

	// Сбор данных с модели формы для отправки на сервер
	const collectFormDataKoVm = ( withSimpleData: boolean = false ) => {
		if ( !formState.rootModel ) return null;
		// Для форм с несколькими страницами DefaultValueScript(визардом) выполняется только при первом переходе на страницу. Если пользователь не посещал некоторые вкладки, их поля остаются null
		// Принудительно инициализируем все страницы
		const pageContainer = formState.rootModel.FormElements?.[0];
		if ( pageContainer instanceof PageContainerViewModel && mediator.current ) {
			pageContainer.Pages.forEach( (_, index) => {
				mediator.current!.setCurrentPageIndex( index );
			} );
		}
		// Рекурсивно обрабатываем элементы, начиная с родительской главной формы
		const finalData = ViewModelDataCollector.collectFormDataKoVm( formState.rootModel );
		// console.log( formState.rootModel, "result", finalData );
		//
		const simpleData = withSimpleData ? ViewModelDataCollector.collectCurrentElementSimpleFormatDataFromKoVm( formState.rootModel ) : null;
		return {
			fileInfos: ViewModelDataCollector.fileInfos, 
			formAttachments: ViewModelDataCollector.formAttachments,
			// TODO
			values: (!formState.rootModel.FormElements || !(formState.rootModel.FormElements[0] as any).Pages) ? finalData : finalData["_"],
			simpleData 
		};
	};

	return { initForm, formState, formValidate, resetForm, collectFormDataKoVm } as const;
};

export default useDynamicForm;
